import { Skeleton, styled } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloudReader from 'shared/_model/cloud/CloudReader';
import { Cloud } from 'shared/_model/cloud/types';
import { bufferToBlob } from 'shared/_model/utils/base64'
import { useDB } from 'shared/Providers/UserDB'

const NoImage = styled('div', { name: 'NoImage' })(() => ({
	backgroundColor: grey[800],
	height: '100%',
	width: '100%',
}));

const Image = styled('img', { name: 'Image' })(() => ({
	height: 'auto',
	objectFit: 'cover',
	width: '100%',
}));

type CloudImageProps = Omit<ComponentProps<typeof Image>, 'src'> & {
	image: Cloud | undefined;
};

export default function CloudImage({ image, ...props }: CloudImageProps) {
	const [isLoading, setIsLoading] = useState(true);
	const [src, setSrc] = useState<string>('');

	const handleGetUrl = useCallback(async () => {
		setIsLoading(true);
		const url = await new CloudReader(image).getUrl();
		setSrc(url);

		if (!url) {
			setIsLoading(false);
			return;
		}

		// Preload the image so we can wait for its onload event
		// without needing it to be rendered in the DOM first.
		const img = new window.Image();
		img.onload = () => setIsLoading(false);
		img.onerror = () => setIsLoading(false);
		img.src = url;
	}, [image]);

	useEffect(() => {
		handleGetUrl();
	}, [handleGetUrl]);

	if (isLoading) {
		return <Skeleton sx={{ transform: 'none', width: '100%' }} />;
	}

	if (!src) {
		return <NoImage sx={props.sx} />;
	}

	return <Image src={src} alt="thumbnail" {...props} />;
}

type AttachmentImageProps = Omit<ComponentProps<typeof Image>, 'src'> & {
	attachments?: PouchDB.Core.Attachments;
	name: string;
	docId: string;
};

export function AttachmentImage({ attachments, name, docId, ...props }: AttachmentImageProps) {
	const { db } = useDB();
	const [isLoading, setIsLoading] = useState(true);
	const [src, setSrc] = useState<string>('');
	const attachment = attachments?.[name];

	const handleGetUrl = useCallback(async () => {
		let att = attachment;
		if (!att) {
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		if ('stub' in att) {
			let data = await db?.getAttachment(docId, name);
			console.log('tst: att data', data);
			if (!data) {
				setIsLoading(false);
				return;
			}
			if (!(data instanceof Blob)) {
				data = bufferToBlob(data, att.content_type);
			}
			att = { ...att, data } as PouchDB.Core.FullAttachment;
		}

		if (!att.data) {
			setIsLoading(false);
			return;
		}

		console.log('tst: att', att);
		let url: string;
		if (typeof att.data === 'string') {
			url = `data:${att.content_type};base64,${att.data}`;
		} else {
			url = URL.createObjectURL(att.data as Blob);
		}
		setSrc(url);

		if (!url) {
			setIsLoading(false);
			return;
		}

		setIsLoading(false);
	}, [attachment, db, docId, name]);

	useEffect(() => {
		handleGetUrl();
	}, [handleGetUrl]);

	if (isLoading) {
		return <Skeleton sx={{ transform: 'none', width: '100%' }} />;
	}

	if (!src) {
		return <NoImage sx={props.sx} />;
	}

	return <Image src={src} alt="thumbnail" {...props} />;
}
